import { useRouter } from "next/router";

import { AxiosError } from "axios";

import { useQueryClient } from "@enkor/react-query";

import { GA_EVENT_IDS } from "@domains/common/constants/googleAnalyticsIds";
import {
  getColorButtonAnimation,
  iconButtonAnimation,
} from "@domains/common/utils/animationHelper";
import { grayScale, pink } from "@enkor/design/colors";
import { LoadingSpinner } from "@enkor/design/components";
import { Share2Icon } from "@enkor/design/icons";
import { useCreateEventRegisterMutation } from "@domains/event/network/eventMutations";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { eventQueryKeys } from "@domains/event/constants/eventQueryKeys";
import { myPageQueryKeys } from "@domains/myPage/constants/myPageQueryKeys";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";
import WebViewBridge from "@logics/utils/webViewBridge";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { EVENT_MESSAGE } from "@domains/event/constants/toastMessages";
import useModalState from "@logics/hooks/useModalState";
import { ErrorResponseType } from "@models/client";
import NoticeModal from "@domains/common/views/modal/NoticeModal/NoticeModal";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { ERROR_CODE } from "@domains/common/constants/errors";
import EventViewModel from "@domains/event/viewModel/EventViewModel";
import { ErrorCodeType } from "@domains/common/types/common";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { EventInfoTrackingDataType } from "@domains/event/types/event";
import { useFetchRecentNoShowEventQuery } from "@domains/event/network/eventQueries";

import NoShowReasonBottomSheet from "../../NoShowReasonBottomSheet/NoShowReasonBottomSheet";

import * as S from "./ParticipateButtonGroup.style";

interface ParticipateButtonGroupProps {
  eventId: string;
  eventInfo: EventInfoTrackingDataType;
  hostInfo: EventViewModel["host"];
  afterJoin: () => void;
  onClickShare: () => void;
}

const ParticipateButtonGroup = ({
  eventId,
  eventInfo,
  hostInfo,
  afterJoin,
  onClickShare,
}: ParticipateButtonGroupProps) => {
  const queryClient = useQueryClient();

  const { replace } = useRouter();

  const { user, guardRoute } = useAuthSession();
  const { openModal, closeModal } = useModalState();

  const { data: recentNoShowEventList } =
    useFetchRecentNoShowEventQuery(eventId);

  const registerMutation = useCreateEventRegisterMutation();

  const handleJoinError = (errorCode?: ErrorCodeType) => {
    switch (errorCode) {
      case ERROR_CODE.EV008:
        openModal({
          CustomComponent: (
            <NoticeModal
              modal={{
                title: "User Blocking Notice",
                description:
                  "Due to personal settings between users who participated in this event, we are unable to proceed with the event. Thank you for your understanding. Explore other events and enjoy!",
              }}
              ctaButton={{
                onClickConfirm: () => {
                  closeModal();

                  if (WebViewBridge.isInAppWebView()) {
                    WebViewBridge.getInstance().navigateToMain();
                  } else {
                    replace(NAVIGATION_ROUTE.EVENT_LIST.HREF);
                  }
                },
              }}
              closeButton={{
                withXIcon: true,
              }}
            />
          ),
        });
        break;
      case ERROR_CODE.EV011:
        ToastError(EVENT_MESSAGE.DUPLICATE_JOIN);
        break;
      default:
        ToastError(ERROR_MESSAGE.SERVER_ERROR);
        break;
    }
  };

  const handleClickParticipate = () =>
    guardRoute(async () => {
      if (registerMutation.isPending) return;

      try {
        const submitEventParticipation = async () => {
          await registerMutation.mutateAsync(eventId);

          queryClient.invalidateQueries({
            queryKey: eventQueryKeys.getEventDetail({ eventId }),
          });
          queryClient.invalidateQueries({
            queryKey: myPageQueryKeys.getEventList({ participating: true }),
          });

          dataTrackEvent({
            eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_JOIN,
            properties: {
              hasProfile: user?.hasProfile,
              ...eventInfo,
              hostId: hostInfo.id,
              hostNickname: hostInfo.name,
              hostGender: hostInfo.genderForDisplay,
              hostNationality: hostInfo.nationality,
            },
          });
        };

        if (WebViewBridge.isInAppWebView()) {
          const result = await WebViewBridge.getInstance().triggerEventJoin();

          if (result === "success") {
            await submitEventParticipation();
            ToastSuccess(EVENT_MESSAGE.JOIN_COMPLETED);
            closeModal();
            afterJoin();
          }
        } else {
          await submitEventParticipation();
          ToastSuccess(EVENT_MESSAGE.JOIN_COMPLETED);
          closeModal();
          afterJoin();
        }
      } catch (error) {
        const { response } = error as AxiosError<ErrorResponseType>;
        handleJoinError(response?.data?.errorCode);
      }
    });

  const handleRequestNoShowReason = () => {
    if (!recentNoShowEventList || recentNoShowEventList?.length === 0) return;

    openModal({
      CustomComponent: (
        <NoShowReasonBottomSheet
          noShowEventId={recentNoShowEventList[0].id}
          eventInfo={eventInfo}
          onSubmitReason={handleClickParticipate}
        />
      ),
    });
  };

  return (
    <S.ButtonGroupWrapper>
      <S.ShareButtonBox
        type="button"
        id={GA_EVENT_IDS.EVENT.GUEST_SHARE}
        {...iconButtonAnimation}
        onClick={onClickShare}
      >
        <Share2Icon strokeColor={grayScale.white} size={20} />
      </S.ShareButtonBox>
      <S.FullButton
        type="button"
        id={GA_EVENT_IDS.EVENT.GUEST_JOIN}
        {...getColorButtonAnimation({
          hoverColor: pink[300],
          tapColor: pink[200],
        })}
        onClick={
          (recentNoShowEventList?.length || 0) > 0
            ? handleRequestNoShowReason
            : handleClickParticipate
        }
      >
        {registerMutation.isPending ? (
          <LoadingSpinner loaderType="SyncLoader" color={grayScale.white} />
        ) : (
          "Join"
        )}
      </S.FullButton>
    </S.ButtonGroupWrapper>
  );
};

export default ParticipateButtonGroup;
